import { detailPanelVariants } from 'animations/motionConfig';
import { AnimatePresence, motion } from 'framer-motion';
import { ChevronDown, ExternalLink, X } from 'lucide-react';
import React, { FC, useCallback, useEffect, useState } from 'react';
import ViewPdfLink from 'components/ui/ViewPdfLink';
import { isPdfPath, resolveUploadFileUrl } from 'helpers/resolveUploadFileUrl';
import ExpandableDetailText, { isWideDetailField } from './ExpandableDetailText';
import type { DetailField } from './flattenRecordForDetail';

interface RowDetailPanelProps {
    fields: DetailField[];
    title?: string;
}

const ImageLightbox: FC<{ src: string; onClose: () => void }> = ({ src, onClose }) => {
    useEffect(() => {
        const onKey = (e: KeyboardEvent) => {
            if (e.key === 'Escape') {
                onClose();
            }
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [onClose]);

    return (
        <motion.div
            className="row-detail-lightbox"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            role="dialog"
            aria-modal="true"
            aria-label="Image preview"
        >
            <button type="button" className="row-detail-lightbox__close" onClick={onClose} aria-label="Close">
                <X className="size-5" />
            </button>
            <motion.img
                src={src}
                alt="Preview"
                className="row-detail-lightbox__img"
                initial={{ scale: 0.92, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.92, opacity: 0 }}
                transition={{ duration: 0.2 }}
                onClick={(e) => e.stopPropagation()}
            />
        </motion.div>
    );
};

const DetailFieldValue: FC<{ field: DetailField; onPreviewImage?: (src: string) => void }> = ({
    field,
    onPreviewImage,
}) => {
    const [imgError, setImgError] = useState(false);

    const { type, value, href, label } = field;
    const rawPath = value && value !== '—' ? value : href;
    const fileHref = rawPath ? resolveUploadFileUrl(rawPath) : undefined;

    if (type === 'image' && fileHref && !imgError) {
        return (
            <button
                type="button"
                className="row-detail-panel__image-btn"
                onClick={(e) => {
                    e.stopPropagation();
                    onPreviewImage?.(fileHref);
                }}
                title="Click to view full image"
            >
                <img
                    src={fileHref}
                    alt={label}
                    className="row-detail-panel__thumb"
                    onError={() => setImgError(true)}
                />
                <span className="row-detail-panel__image-hint">View image</span>
            </button>
        );
    }

    if (type === 'image' && imgError && fileHref) {
        return (
            <a href={fileHref} target="_blank" rel="noreferrer" className="row-detail-panel__link">
                <ExternalLink className="inline size-3.5" /> Open file
            </a>
        );
    }

    if (type === 'pdf' && rawPath) {
        return <ViewPdfLink path={rawPath} />;
    }

    if (type === 'url' && fileHref && (fileHref.includes('/uploads/') || fileHref.includes('/app/uploads/'))) {
        if (isPdfPath(rawPath)) {
            return <ViewPdfLink path={rawPath} />;
        }
        return (
            <ExpandableDetailText
                value={value}
                href={fileHref}
                linkClassName="row-detail-panel__link row-detail-panel__value--wrap break-all"
            />
        );
    }

    if (type === 'url' && href && (href.includes('/uploads/') || href.includes('/app/uploads/'))) {
        const resolved = resolveUploadFileUrl(href);
        if (isPdfPath(href)) {
            return <ViewPdfLink path={href} />;
        }
        return (
            <ExpandableDetailText
                value={value}
                href={resolved}
                linkClassName="row-detail-panel__link row-detail-panel__value--wrap break-all"
            />
        );
    }

    if (type === 'url' && href) {
        return <ExpandableDetailText value={value} href={href} />;
    }

    if (type === 'long-text') {
        return <ExpandableDetailText value={value} href={href} />;
    }

    return <ExpandableDetailText value={value} />;
};

const RowDetailPanel: FC<RowDetailPanelProps> = ({ fields, title = 'Row details' }) => {
    const [previewSrc, setPreviewSrc] = useState<string | null>(null);
    const onPreviewImage = useCallback((src: string) => setPreviewSrc(src), []);

    if (fields.length === 0) {
        return <p className="text-sm text-slate-500">No additional details.</p>;
    }

    const content = (
        <div className="row-detail-panel">
            <div className="row-detail-panel__header">
                <ChevronDown className="row-detail-panel__icon size-4 text-[#bd902e]" aria-hidden />
                <span className="text-sm font-semibold text-[#bd902e]">{title}</span>
            </div>
            <dl className="row-detail-panel__grid">
                {fields.map((field) => (
                    <div
                        key={field.label}
                        className={`row-detail-panel__item${isWideDetailField(field) ? ' row-detail-panel__item--full' : ''}`}
                    >
                        <dt className="row-detail-panel__label">{field.label}</dt>
                        <dd className="row-detail-panel__value-cell">
                            <DetailFieldValue field={field} onPreviewImage={onPreviewImage} />
                        </dd>
                    </div>
                ))}
            </dl>
            <AnimatePresence>
                {previewSrc && <ImageLightbox src={previewSrc} onClose={() => setPreviewSrc(null)} />}
            </AnimatePresence>
        </div>
    );

    return (
        <motion.div variants={detailPanelVariants} initial="hidden" animate="show" exit="hidden">
            {content}
        </motion.div>
    );
};

export default RowDetailPanel;
